import React from "react";
import { InternalLayout } from "../Components/InternalLayout";
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Grid,
  Card,
  CardContent,
  Avatar,
  Divider,
  IconButton,
} from "@mui/material";
import {
  Dashboard as DashboardIcon,
  ExitToApp,
  Person,
  Security,
  Settings,
  Notifications,
  BarChart,
  People,
  Inventory,
  Assignment,
  VideoLibrary,
} from "@mui/icons-material";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import DashboardButton from "../Components/DashboardButton";
import { StatCard } from "../Components/StatCard";
import theme from "../Theme";

export function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const stats = [
    {
      title: "Total de Membros",
      value: "200",
      color: theme.palette.primary.main,
    },
    { title: "Eventos Agendados", value: "1", color: "#2E7D32" },
    { title: "Mensagens", value: "0", color: "#1976D2" },
    { title: "Relatórios", value: "0", color: "#F57C00" },
  ];

  return (
    <InternalLayout>
      <Paper
        elevation={1}
        sx={{
          backgroundColor: "primary.main",
          color: "white",
          py: { xs: 1.5, md: 2 },
          mb: 4,
        }}
      >
        <Container sx={{ px: { xs: 2, sm: 3 } }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", md: "center" },
              flexDirection: { xs: "column", md: "row" },
              gap: { xs: 2, md: 0 },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                minWidth: 0,
                flex: 1,
              }}
            >
              <DashboardIcon
                sx={{ fontSize: { xs: 28, md: 40 }, mr: { xs: 1, md: 2 } }}
              />
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography
                  variant="h5"
                  fontWeight="bold"
                  sx={{
                    fontSize: { xs: "1.25rem", md: "2.125rem" },
                    lineHeight: { xs: 1.2, md: 1.235 },
                  }}
                >
                  Painel Administrativo
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    opacity: 0.9,
                    fontSize: { xs: "0.75rem", md: "0.875rem" },
                  }}
                >
                  Portal ZS - Sistema de Gestão
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: { xs: 1, md: 2 },
                width: { xs: "100%", md: "auto" },
                justifyContent: { xs: "space-between", md: "flex-end" },
              }}
            >
              <Box
                sx={{
                  textAlign: { xs: "left", md: "right" },
                  flex: { xs: 1, md: "none" },
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ fontSize: { xs: "1rem", md: "1.25rem" } }}
                >
                  {user?.name}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    opacity: 0.8,
                    fontSize: { xs: "0.75rem", md: "0.875rem" },
                  }}
                >
                  {user?.role === "admin" ? "Administrador" : "Secretaria"}
                </Typography>
              </Box>
              <Avatar
                sx={{
                  bgcolor: "rgba(255,255,255,0.2)",
                  width: { xs: 32, md: 40 },
                  height: { xs: 32, md: 40 },
                }}
              >
                <Person sx={{ fontSize: { xs: 16, md: 20 } }} />
              </Avatar>
              <IconButton
                color="inherit"
                onClick={handleLogout}
                sx={{
                  ml: { xs: 0.5, md: 1 },
                  padding: { xs: 1, md: 1.5 },
                }}
                title="Sair"
              >
                <ExitToApp sx={{ fontSize: { xs: 18, md: 24 } }} />
              </IconButton>
            </Box>
          </Box>
        </Container>
      </Paper>

      <Container sx={{ px: { xs: 2, sm: 3 } }}>
        {/* Welcome Section */}
        <Paper
          sx={{
            p: { xs: 2, md: 3 },
            mb: 4,
            borderLeft: "4px solid primary.main",
          }}
        >
          <Typography
            variant="h5"
            gutterBottom
            sx={{ fontSize: { xs: "1.25rem", md: "1.5rem" } }}
          >
            Bem-vindo(a), {user?.name}! 👋
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ fontSize: { xs: "0.875rem", md: "1rem" } }}
          >
            Você está logado como{" "}
            <strong>
              {user?.role === "admin" ? "Administrador" : "Secretaria"}
            </strong>{" "}
            desde {new Date().toLocaleDateString("pt-BR")}. Este é seu painel de
            controle para gerenciar o Portal ZS.
          </Typography>
        </Paper>

        {/* Stats Cards */}
        <Grid container spacing={{ xs: 2, md: 3 }} sx={{ mb: 4 }}>
          {stats.map((stat, index) => (
            <Grid item xs={6} sm={6} md={3} key={index}>
              <StatCard stat={stat} />
            </Grid>
          ))}
        </Grid>

        {/* Quick Actions */}
        <Paper sx={{ p: { xs: 2, md: 3 }, mb: 4 }}>
          <Typography
            variant="h6"
            gutterBottom
            sx={{
              display: "flex",
              alignItems: "center",
              fontSize: { xs: "1.1rem", md: "1.25rem" },
            }}
          >
            <Settings
              sx={{
                mr: 1,
                color: "primary.main",
                fontSize: { xs: 20, md: 24 },
              }}
            />
            Ações Rápidas
          </Typography>
          <Divider sx={{ mb: { xs: 2, md: 3 } }} />

          <Grid container spacing={{ xs: 1.5, md: 2 }}>
            {/* Botão de Gerenciar Usuários - Apenas para Admin */}
            {user?.role === "admin" && (
              <DashboardButton
                Icon={Assignment}
                text="Gerenciar Usuários"
                navigateTo="/admin/users"
                navigate={navigate}
                color="primary.main"
                hoverColor="primary.dark"
              />
            )}

            {/* Botão de Gerenciar Produtos - Apenas para Admin */}
            {user?.role === "admin" && (
              <DashboardButton
                Icon={Assignment}
                text="Gerenciar Produtos"
                navigateTo="/admin/produtos"
                navigate={navigate}
                color="primary.main"
                hoverColor="primary.dark"
              />
            )}

            {/* Botão de Gerenciar Ministrações - Apenas para Admin */}
            {user?.role === "admin" && (
              <DashboardButton
                Icon={VideoLibrary}
                text="Ministrações"
                navigateTo="/admin/ministracoes"
                navigate={navigate}
                color="primary.main"
                hoverColor="primary.dark"
              />
            )}

            {/* Botão de Relatório de Inscrições - Apenas para Admin */}
            {user?.role === "admin" && (
              <DashboardButton
                Icon={Assignment}
                text="Relatório Inscrições"
                navigateTo="/admin/inscricoes"
                navigate={navigate}
                color="primary.main"
                hoverColor="primary.dark"
              />
            )}

            {/* Botão de Gerenciar Avisos - Apenas para Admin */}
            {user?.role === "admin" && (
              <DashboardButton
                Icon={Person}
                text="Gerenciar Avisos"
                navigateTo="/announcements"
                navigate={navigate}
                color="primary.main"
                hoverColor="primary.dark"
              />
            )}

            {/* Botão de Gerenciar Mensagens - Apenas para Admin */}
            {user?.role === "admin" && (
              <DashboardButton
                Icon={Person}
                text="Gerenciar Mensagens"
                navigateTo="/mensagens"
                navigate={navigate}
                color="primary.main"
                hoverColor="primary.dark"
              />
            )}
          </Grid>
        </Paper>

        {/* User Info */}
        <Paper sx={{ p: { xs: 2, md: 3 } }}>
          <Typography
            variant="h6"
            gutterBottom
            sx={{
              display: "flex",
              alignItems: "center",
              fontSize: { xs: "1.1rem", md: "1.25rem" },
            }}
          >
            <Person
              sx={{
                mr: 1,
                color: "primary.main",
                fontSize: { xs: 20, md: 24 },
              }}
            />
            Informações da Sessão
          </Typography>
          <Divider sx={{ mb: { xs: 2, md: 3 } }} />

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: { xs: 1.5, md: 2 },
            }}
          >
            <Box>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: { xs: "0.8rem", md: "0.875rem" } }}
              >
                <strong>ID do Usuário:</strong> {user?.id}
              </Typography>
            </Box>
            <Box>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: { xs: "0.8rem", md: "0.875rem" } }}
              >
                <strong>Email:</strong> {user?.email}
              </Typography>
            </Box>
            <Box>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: { xs: "0.8rem", md: "0.875rem" } }}
              >
                <strong>Nível de Acesso:</strong>{" "}
                {user?.role === "admin" ? "Administrador" : "Secretaria"}
              </Typography>
            </Box>
            <Box>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: { xs: "0.8rem", md: "0.875rem" } }}
              >
                <strong>Última Login:</strong>{" "}
                {new Date().toLocaleString("pt-BR")}
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Container>
    </InternalLayout>
  );
}
